<template>
  <div v-if="isPasswordProtected" class="flex h-dvh items-center justify-center bg-background">
    <div class="container">
      <div class="mx-auto max-w-96">
        <PasswordProtectForm />
      </div>
    </div>
  </div>
  <div v-else :class="backgroundClass" class="relative min-h-dvh bg-cover">
    <Header
      v-if="!route.meta.noHeader && isAuthenticated"
      :has-back-button="route.name === 'FanzonePost'"
    />
    <router-view v-slot="{ Component }">
      <component :is="Component" :key="$route.path" class="z-10" />
    </router-view>
    <Footer v-if="!route.meta.noFooter" />
    <div
      class="pointer-events-none fixed top-0 -z-10 h-screen w-screen"
      :class="backgroundClass"
    ></div>
    <NotForYou
      v-if="!route.meta.preview && isAuthenticated && emailMismatch"
      :isOpen="emailMismatch"
    />
    <MissingDetails
      v-else-if="!route.meta.preview && isAuthenticated && hasMissingFields"
      :isOpen="hasMissingFields"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue';
import { useCookies } from '@vueuse/integrations/useCookies';
import { useFanStore } from '@stores/fan.store';
import { useRoute } from 'vue-router';
import { useAccountStore } from '@stores/account.store';
import { storeToRefs } from 'pinia';

import Header from '@generics/Header.vue';
import Footer from '@generics/Footer.vue';
import PasswordProtectForm from '@modules/PasswordProtectForm.vue';
import MissingDetails from '@modules/MissingDetails/MissingDetails.vue';
import NotForYou from '@modules/NotForYou/NotForYou.vue';

const route = useRoute();

const cookies = useCookies(['stored-password']);
const fanStore = useFanStore();
const { hasMissingFields } = storeToRefs(fanStore);
const accountStore = useAccountStore();
const { isAuthenticated } = storeToRefs(accountStore);

const emailMismatch = computed(
  () =>
    accountStore.guestEmail && fanStore.fanEmail && accountStore.guestEmail !== fanStore.fanEmail,
);

const isPasswordProtected = computed(
  () =>
    import.meta.env.VITE_FF_PASSWORD &&
    cookies.get('stored-password') !== import.meta.env.VITE_FF_PASSWORD,
);

const backgroundClass = computed(() => {
  const backgroundVariants = {
    black: 'bg-black flex flex-col min-h-dvh',
    paper: `bg-black bg-[url('../feed-bg.png')] bg-no-repeat bg-cover bg-fixed flex flex-col`,
    profile: `bg-black bg-[url('../sign-up.png')]  bg-no-repeat bg-cover bg-fixed flex flex-col`,
    default: 'bg-background',
  };

  switch (route.name) {
    case 'Home':
      return backgroundVariants.black;
    case 'FanzoneIndex':
    case 'FanzonePost':
      return backgroundVariants.paper;
    case 'Profile':
      return backgroundVariants.profile;
    case 'Login':
      return backgroundVariants.profile;
    case 'Signup':
      return backgroundVariants.profile;
    default:
      return backgroundVariants.default;
  }
});

onMounted(async () => {
  if (isPasswordProtected.value && cookies.get('stored-password')) {
    cookies.remove('stored-password');
  }
});
</script>
